import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role hierarchy:
 *   admin    – full access (view, create, edit, delete, manage team, audit logs)
 *   operator – can view, create, edit; cannot delete or manage team; can see audit logs
 *   viewer   – read-only; no create/edit/delete/manage team; no audit logs
 */
const ROLE_PERMISSIONS = {
  admin: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canManageTeam: true,
    canViewAuditLog: true,
  },
  operator: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: false,
    canManageTeam: false,
    canViewAuditLog: true,
  },
  viewer: {
    canView: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManageTeam: false,
    canViewAuditLog: false,
  },
};

const DEFAULT_PERMISSIONS = {
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canManageTeam: false,
  canViewAuditLog: false,
  role: null,
  loading: true,
};

export const usePermissions = () => {
  const { user, isDemoMode } = useAuth();
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [refreshTick, setRefreshTick] = useState(0);

  const refreshPermissions = useCallback(() => {
    setRefreshTick(t => t + 1);
  }, []);

  useEffect(() => {
    if (!user) {
      setPermissions({ ...DEFAULT_PERMISSIONS, loading: false });
      return;
    }

    // Demo mode defaults to admin
    if (isDemoMode) {
      setPermissions({
        ...ROLE_PERMISSIONS?.admin,
        role: 'admin',
        loading: false,
      });
      return;
    }

    const fetchRole = async () => {
      try {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', user?.id)
          ?.maybeSingle();

        if (error) {
          console.error('usePermissions: error fetching role', error?.message);
          // Default to viewer on error for safety
          setPermissions({ ...ROLE_PERMISSIONS?.viewer, role: 'viewer', loading: false });
          return;
        }

        const role = data?.role || 'viewer';
        const perms = ROLE_PERMISSIONS?.[role] || ROLE_PERMISSIONS?.viewer;
        setPermissions({ ...perms, role, loading: false });
      } catch (err) {
        console.error('usePermissions: unexpected error', err);
        setPermissions({ ...ROLE_PERMISSIONS?.viewer, role: 'viewer', loading: false });
      }
    };

    fetchRole();
  }, [user, isDemoMode, refreshTick]);

  return { ...permissions, refreshPermissions };
};

export default usePermissions;
